/**
 * Invoice Detail Page - Consultant
 * Shows detailed view of an invoice with actions
 */

import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Send,
  Edit,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Printer,
} from 'lucide-react';
import { mockInvoices } from '../../data/payments';
import { InvoiceTemplate } from '../../components/payment';
import { InvoiceStatus } from '../../types/payment';

export const InvoiceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const printRef = useRef<HTMLDivElement>(null);

  // Find invoice
  const invoice = useMemo(() => {
    return mockInvoices.find((inv) => inv.id === invoiceId);
  }, [invoiceId]);

  const handleBack = () => {
    navigate('/consultant/invoices');
  };

  const handleDownload = () => {
    if (!invoice) return;
    console.log('Downloading invoice:', invoice.invoiceNumber);
    alert(`Downloading ${invoice.invoiceNumber}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendInvoice = () => {
    if (!invoice) return;
    console.log('Sending invoice:', invoice.invoiceNumber);
    alert(`Sending invoice ${invoice.invoiceNumber} to ${invoice.billTo.email}`);
  };

  const handleEditInvoice = () => {
    if (!invoice) return;
    navigate(`/consultant/invoices/${invoice.id}/edit`);
  };

  const handleDeleteInvoice = () => {
    if (!invoice) return;
    if (confirm(t('payments:invoice.confirmDelete'))) {
      console.log('Deleting invoice:', invoice.invoiceNumber);
      navigate('/consultant/invoices');
    }
  };

  const handleMarkAsPaid = () => {
    if (!invoice) return;
    if (confirm(t('payments:invoice.confirmMarkPaid'))) {
      console.log('Marking invoice as paid:', invoice.invoiceNumber);
      alert(t('payments:invoice.markedAsPaid'));
    }
  };

  if (!invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('payments:invoice.notFound')}
          </h2>
          <p className="text-gray-600 mb-4">{t('payments:invoice.notFoundDescription')}</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {t('payments:invoice.backToList')}
          </button>
        </div>
      </div>
    );
  }

  const isOverdue =
    invoice.status !== InvoiceStatus.PAID &&
    invoice.status !== InvoiceStatus.CANCELLED &&
    new Date(invoice.dueDate) < new Date();

  const canEdit = invoice.status === InvoiceStatus.DRAFT;
  const canSend =
    invoice.status === InvoiceStatus.DRAFT || invoice.status === InvoiceStatus.ISSUED;
  const canMarkPaid =
    invoice.status === InvoiceStatus.ISSUED || invoice.status === InvoiceStatus.SENT;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header - Non-printable */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
            <p className="text-gray-600 mt-1">
              {t('payments:invoice.billTo')}: {invoice.billTo.name}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            title={t('common.print')}
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownload}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            title={t('common.download')}
          >
            <Download className="w-4 h-4" />
          </button>
          {canSend && (
            <button
              onClick={handleSendInvoice}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {t('payments:invoice.send')}
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleEditInvoice}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>

      {/* Status Alert - Non-printable */}
      {isOverdue && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 print:hidden">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-900">{t('payments:invoice.overdueAlert')}</h3>
            <p className="text-sm text-red-700">{t('payments:invoice.overdueDescription')}</p>
          </div>
        </div>
      )}

      {invoice.status === InvoiceStatus.DRAFT && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3 print:hidden">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-yellow-900">{t('payments:invoice.draftStatus')}</h3>
            <p className="text-sm text-yellow-700">{t('payments:invoice.draftDescription')}</p>
          </div>
        </div>
      )}

      {invoice.status === InvoiceStatus.PAID && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 print:hidden">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-green-900">{t('payments:invoice.paidStatus')}</h3>
            <p className="text-sm text-green-700">{t('payments:invoice.paidDescription')}</p>
          </div>
        </div>
      )}

      {/* Invoice Template */}
      <div ref={printRef}>
        <InvoiceTemplate invoice={invoice} />
      </div>

      {/* Actions - Non-printable */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 print:hidden">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('payments:invoice.actions')}</h3>
        <div className="flex gap-3 flex-wrap">
          {canMarkPaid && (
            <button
              onClick={handleMarkAsPaid}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {t('payments:invoice.markAsPaid')}
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleEditInvoice}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              {t('common.edit')}
            </button>
          )}
          {invoice.status === InvoiceStatus.DRAFT && (
            <button
              onClick={handleDeleteInvoice}
              className="px-6 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {t('common.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
